import * as Sentry from "@sentry/react";

import { StrictMode, Suspense, useEffect } from "react";
import * as ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router";
import "./app/i18n";
import ErrorFallback from "@components/ErrorFallback";
import { getTheme } from "@components/Header/ThemeSelector";
import LoadingSpinner from "@components/LoadingSpinner";
import Toast from "@components/Toast";
import { AuthProvider } from "react-oidc-context";
import { environment } from "./app/environment";
import { store } from "./app/store/store";
import { router } from "./routes/router";

Sentry.init({
  dsn: "https://2d741d8416a8ba2d0e80f5acbd9589b4@o4508324830707712.ingest.de.sentry.io/4508324987535440",
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation: useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: environment.sentrySampleRate,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/(?:staging\.)tumtutor\.(?:ai|de)\/api/,
  ],
  environment: environment.name,
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

document.querySelector("html")?.setAttribute("data-theme", getTheme());

document.title = environment.instanceConfig.name;
const favicon = document.querySelector('link[rel="icon"]');
if (favicon) {
  favicon.setAttribute("href", environment.instanceConfig.favicon);
}

const App = () => (
  <Suspense fallback={<LoadingSpinner />}>
    <Provider store={store}>
      <AuthProvider {...environment.oidc}>
        <Toast />
        <RouterProvider router={router} />
      </AuthProvider>
    </Provider>
  </Suspense>
);

const ProfiledApp = Sentry.withProfiler(App);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
      <ProfiledApp />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
